/**
 * AASB 124 — Related Party Disclosures. Rulepack v1.0.0.
 * Validates a related-party transactions register: classification, arm's-length
 * flag, Division 7A exposure on intra-group loans, completeness for disclosure.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parseBool, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb124-1.0.0';
const fb = findingBuilder('AASB_124' as any, VERSION);

const HMAP = makeHeaderMap([
  ['transaction_id', 'transaction id', 'id', 'ref'],
  ['party_name', 'party name', 'related party', 'counterparty', 'name'],
  ['party_type', 'party type', 'relationship', 'rp type'],
  ['transaction_type', 'transaction type', 'type'],
  ['amount', 'amount', 'value', 'transaction value'],
  ['balance_outstanding', 'balance outstanding', 'outstanding balance', 'closing balance'],
  ['terms_arms_length', 'arms length', "arm's length", 'market terms'],
  ['div7a_compliant', 'div7a compliant', 'division 7a', 'div 7a'],
  ['nature_disclosed', 'nature disclosed', 'disclosed in notes', 'disclosed'],
]);

const REQUIRED = ['party_name', 'party_type', 'transaction_type', 'amount'];

const KNOWN_PARTY_TYPES = new Set([
  'kmp', 'key_management_personnel', 'director', 'parent', 'subsidiary', 'associate',
  'joint_venture', 'jv', 'related_entity', 'related_party', 'other', 'shareholder', 'family_member',
]);

const KNOWN_TRANSACTION_TYPES = new Set([
  'sale', 'purchase', 'loan_to', 'loan_from', 'lease', 'service', 'guarantee',
  'remuneration', 'dividend', 'distribution', 'expense_reimbursement', 'other',
]);

export function detectAASB124(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  return { confidence: matched.length / REQUIRED.length, matched, missing: REQUIRED.filter(r => !hits.has(r)) };
}

export function validateAASB124(headers: string[], data: CellValue[][], _asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_124' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('transaction_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB124.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const ptype = String(get('party_type') ?? '').toLowerCase().replace(/[\s-]+/g, '_');
    const ttype = String(get('transaction_type') ?? '').toLowerCase().replace(/[\s-]+/g, '_');
    const amount = parseNumber(get('amount')) ?? 0;
    const balance = idx.balance_outstanding !== undefined ? parseNumber(get('balance_outstanding')) : null;
    const armsLength = idx.terms_arms_length !== undefined ? parseBool(get('terms_arms_length')) : null;
    const div7a = idx.div7a_compliant !== undefined ? parseBool(get('div7a_compliant')) : null;
    const disclosed = idx.nature_disclosed !== undefined ? parseBool(get('nature_disclosed')) : null;

    // R02 party type recognised
    if (!KNOWN_PARTY_TYPES.has(ptype)) {
      findings.push(fb.build('AASB124.R02', 'info', 'Party type not in standard taxonomy',
        `Transaction ${id}: party_type "${ptype}". Confirm classification under AASB 124.9.`,
        { rowId: id }));
    }

    // R03 transaction type recognised
    if (!KNOWN_TRANSACTION_TYPES.has(ttype)) {
      findings.push(fb.build('AASB124.R03', 'info', 'Transaction type not in standard taxonomy',
        `Transaction ${id}: transaction_type "${ttype}". Common: sale, purchase, loan_to, loan_from, lease, service, guarantee, remuneration, dividend.`,
        { rowId: id }));
    }

    // R04 Division 7A flag — private-company loan to shareholder/director/family
    if (/loan/.test(ttype) && /shareholder|director|family|kmp|key_management/.test(ptype) && amount > 0) {
      if (div7a !== true) {
        findings.push(fb.build('AASB124.R04', 'warning', 'Division 7A exposure on related-party loan',
          `Transaction ${id}: ${ttype} of $${fmt(amount)} to ${ptype}. If lender is a private company, ITAA 1936 Division 7A may treat the loan as a deemed dividend unless it complies with s109N (written agreement, minimum interest, term, repayments). Confirm Div 7A compliance.`,
          { rowId: id, evidence: { ttype, ptype, amount, div7aFlag: div7a } }));
      }
    }

    // R05 not at arm's length — additional disclosure
    if (armsLength === false) {
      findings.push(fb.build('AASB124.R05', 'warning', 'Transaction not at arm\'s length — disclosure required',
        `Transaction ${id}: terms_arms_length=false. AASB 124.18 requires the nature of the relationship and information about transactions / outstanding balances to be disclosed; additional disclosure when terms differ from arm's length.`,
        { rowId: id }));
    }

    // R06 outstanding balance disclosed
    if (balance !== null && Math.abs(balance) > 0 && disclosed === false) {
      findings.push(fb.build('AASB124.R06', 'warning', 'Outstanding balance not flagged as disclosed',
        `Transaction ${id}: outstanding balance $${fmt(balance)} but nature_disclosed=false. AASB 124.18(b) requires year-end balances to be disclosed.`,
        { rowId: id }));
    }

    // R07 KMP remuneration completeness reminder
    if (/kmp|key_management|director/.test(ptype) && /remuneration|salary|bonus/.test(ttype)) {
      findings.push(fb.build('AASB124.R07', 'info', 'KMP remuneration — categorised disclosure required',
        `Transaction ${id}: KMP-related remuneration. AASB 124.17 requires aggregate disclosure split by short-term, post-employment, other long-term, termination, and share-based payments.`,
        { rowId: id }));
    }
  });

  return {
    summary: { standard: 'AASB_124' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
